import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { useQuery } from "@tanstack/react-query";
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import GenericButton from "components/Buttons/GenericButton";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import { listWorkCenters } from "crud/workCenters.crud";
import useFilterSync from "hooks/useFilterSync";
import React, { useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { connect } from "react-redux";
import getDataSetByNameAction from "redux/actions/datasetsAction";
import {
  createWorkCenterAction,
  getFilteredWorkCenterAction,
  getWorkCenterByIdAction,
  updateWorkCenterAction,
} from "redux/actions/workCentersAction";
import FilterSection from "./FilterSection";
import ResultSection from "./ResultSection";
import WorkCenterModal from "./WorkCenterModal";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const DEFAULT_FORM_VALUES = {
  name: "",
  provinceId: "",
  municipalityId: "",
  secondaryActivityId: "",
  pageIndex: 1,
  pageSize: 15,
};

const QUERY_PARAMS_PREFIX = "wc_";

const WorkCentersInner = ({
  handleOpenDialog,
  openDialog,
  handleCloseDialog,
  props,
  isCreating,
  selectedWorkCenter,
  sortColumn,
  sortDirection,
  setSortColumn,
  setSortDirection,
}) => {
  const classes = useStylesGeneric();
  const { getValues, setValue } = useFormContext();

  const { refetch, isLoading } = useQuery(
    ["listWorkCenters"],
    () =>
      listWorkCenters({
        ...getValues(),
        sortColumn: sortColumn,
        sortDirection: sortDirection,
      }),
    {
      staleTime: 0,
      refetchOnMount: true,
      enabled: false,
    }
  );

  const { handleFilterSubmit, handleFilterReset, updatePagination, syncFiltersFromUrl } = useFilterSync({
    defaultValues: DEFAULT_FORM_VALUES,
    syncOnMount: false,
    syncOnUrlChange: true,
    syncPagination: true,
    queryParamsPrefix: QUERY_PARAMS_PREFIX,
    pagination: {
      pageIndexKey: "pageIndex",
      pageSizeKey: "pageSize",
      defaultPageSize: DEFAULT_FORM_VALUES.pageSize,
    },
    onFilterChange: () => {
      setTimeout(() => {
        refetch();
      }, 0);
    },
  });

  React.useEffect(() => {
    syncFiltersFromUrl();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async () => {
    setValue("pageIndex", 1);
    handleFilterSubmit(getValues());
  };

  const handleClearFilters = async () => {
    handleFilterReset();
  };

  const handlePaginationChange = async (pageIndex) => {
    setValue("pageIndex", pageIndex);
    updatePagination(pageIndex, getValues("pageSize"));
    await refetch();
  };

  const handleResetToFirstPage = () => {
    updatePagination(1, getValues("pageSize"));
  };

  return (
    <>
      <Grid
        item
        xs={12}
        md={12}
        style={{
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <PageTitleHeader title="Centros de Trabalho" />
        <GenericButton
          typeSubmit={false}
          size="medium"
          color="tertiary"
          onClick={() => handleOpenDialog(null, true)}
          style={{
            marginBottom: "37px",
          }}
        >
          Criar Centro de Trabalho
        </GenericButton>
      </Grid>
      <Grid item xs={12} md={12}>
        <FilterSection
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          isLoading={isLoading}
          onSearch={handleSearch}
          onClear={handleClearFilters}
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <Typography variant="h3" style={{ marginBottom: "1rem" }}>
          Na tabela em baixo estão listados todos os centros de trabalho que integram a lista que registou na sua área
          de utilizador e serão integrados no relatório RENT. <br />
          Poderá editar as informações directamente entrando no detalhe de cada centro de trabalho. Após gravar ou
          adicionar o centro de trabalho, a lista no menu de utilizadores será actualizada.
        </Typography>
      </Grid>
      <Grid item xs={12} md={12}>
        <Box classes={{ root: classes.tableResultBoxRoot }}>
          <ResultSection
            handleOpenDialog={handleOpenDialog}
            sortColumn={sortColumn}
            setSortColumn={setSortColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            onPaginationChange={handlePaginationChange}
            onResetToFirstPage={handleResetToFirstPage}
          />
        </Box>
      </Grid>
      {openDialog && (
        <WorkCenterModal
          openDialog={openDialog}
          handleCloseDialog={handleCloseDialog}
          getDataSetByName={props.getDataSetByName}
          props={props}
          isCreating={isCreating}
          selectedWorkCenter={selectedWorkCenter}
        />
      )}
    </>
  );
};

const WorkCenters = (props) => {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedWorkCenter, setSelectedWorkCenter] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleOpenDialog = (workCenter, isCreating) => {
    setSelectedWorkCenter(workCenter);
    setIsCreating(isCreating);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedWorkCenter(null);
  };

  const form = useForm({
    mode: "onChange",
    defaultValues: DEFAULT_FORM_VALUES,
  });

  return (
    <FormProvider {...form}>
      <WorkCentersInner
        handleOpenDialog={handleOpenDialog}
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        props={props}
        isCreating={isCreating}
        selectedWorkCenter={selectedWorkCenter}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        setSortColumn={setSortColumn}
        setSortDirection={setSortDirection}
      />
    </FormProvider>
  );
};

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getDataSetByName: (name, addEmptyField) => dispatch(getDataSetByNameAction(name, addEmptyField)),
  createWorkCenter: (data, onCloseAction) => dispatch(createWorkCenterAction(data, onCloseAction)),
  updateWorkCenter: (data) => dispatch(updateWorkCenterAction(data)),
  getWorkCenterById: (id) => dispatch(getWorkCenterByIdAction(id)),
  getFilteredWorkCenters: (data) => dispatch(getFilteredWorkCenterAction(data)),
});
export default connect(mapStateToProps, mapDispatchToProps)(WorkCenters);
